import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { FC, useEffect } from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';

import {
  ssrProduct,
  ssrRelatedProducts,
} from '../../@types/codegen/page';
import {
  ProductPageQuery,
  ProductType,
  AllZonesFragment,
  ProductSearchItemFragment,
} from '../../@types/codegen/graphql';
import { IDigandoErrorProps } from '../../interfaces/error.interface';
import {
  generateProductBundleVariables, getDefaultFilterValues,
  IPropertyFilter,
} from '../../hooks/use-product-search-helper';
import { ContentContainerMax } from '../../layout/container';
import { ProductHeader } from '../../components/header/product-header';
import { ICanonical } from '../../interfaces/canonical.interface';
import { generateProductTitle } from '../../components/product/helper/generate-product-title';
import { defaultTranslationNamespaces } from '../../constants/translations';
import { serverSideNavigation } from '../../lib/strapi/server-side-navigation';
import { getStatusCode, IError } from '../../lib/get-status-code';
import { Languages } from '../../types/languages.type';
import { trackProductDetail } from '../../lib/google/tracking-events';
import dayjs from 'dayjs';
import { IDigandoBasicProps } from '../../interfaces/basic-props.interface';

import { generateWeekends } from '../../hooks/use-date-info';
import { i18nextConfig } from '../../next-i18next.config';
import { getCanonicalQueryParams } from '../../lib/getCanonicalQueryParams';
import { ProductDetailSchema } from '../../components/product-detail/product-detail.schema';
import { useRouter } from 'next/router';
import { useGlobalSearchValues, useSetGlobalSearch } from '../../store/recoil/global-search';
import { useSetProductDetails } from '../../store/recoil/product-details';
import { SearchInputTermTypes } from '../../store/global-search.context';

import { NoProductsAvailableLayout } from '../../components/product-detail/no-products-available.layout';
import { DisplayProductDetailLayout } from '../../components/product-detail/display-product-detail.layout';
import { IStrapiSeo, StrapiSeo } from '../../seo/strapi-seo';
import { Zones } from '../../zones/zones';
import { getZoneApiCache } from '../../zones/zone-cache';
import { DetailInformation } from '../../components/product-detail/detail-information';
import { getServerSession } from 'next-auth';
import { authOptions } from '../api/auth/[...nextauth]';
import { getCookies } from 'cookies-next';

// @todo: Check if the dynamic import works correctly with React 18.3.0
// https://digandos.atlassian.net/browse/DD-1881
// const NoProductsAvailableLayout = dynamic(() => import('../../components/product-detail/no-products-available.layout').then((mod) => mod.NoProductsAvailableLayout), { ssr: true });
// const DisplayProductDetailLayout = dynamic(() => import('../../components/product-detail/display-product-detail.layout').then((mod) => mod.DisplayProductDetailLayout), { ssr: true });

interface ISsrData {
  hasAvailableProducts: boolean;
  relatedProducts: ProductSearchItemFragment[];
  productBundle: ProductPageQuery['productBundle'];
  productPrice: ProductPageQuery['productPrice'];
}

interface IProductProps extends IDigandoBasicProps<ISsrData>, IStrapiSeo {
  content: AllZonesFragment[];
}

const Product: FC<IProductProps> = (props) => {
  const { hasAvailableProducts, productBundle, productPrice } = props.data;

  const router = useRouter();
  const { setNotFoundHintVisible } = useSetProductDetails();
  const { setTerm, setMainSearchVisible } = useSetGlobalSearch();
  const { location } = useGlobalSearchValues();

  useEffect(() => {
    if (false === hasAvailableProducts) {
      const productTitle = productBundle?.bundle ? generateProductTitle(productBundle?.bundle.type, productBundle.bundle.properties) : '';

      setMainSearchVisible(false);
      setNotFoundHintVisible(true);
      setTerm({
        value: productTitle,
        type: SearchInputTermTypes.TEXT,
      });
    } else {
      setNotFoundHintVisible(false);
      setMainSearchVisible(true);
    }

    return () => setNotFoundHintVisible(false);
  }, [hasAvailableProducts]);

  useEffect(() => {
    if (productBundle) {
      trackProductDetail(productBundle);
    }

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [router.asPath]);

  return (
    <>
      <ProductDetailSchema productBundle={productBundle} />

      <StrapiSeo
        pageTitle={props.pageTitle}
        seo={props.seo}
        resolverData={{
          product: productBundle?.bundle?.type.title ?? '',
          category: productBundle?.bundle?.type.mainCategory?.searchTitle ?? '',
          location: location?.name ?? '',
          manufacturer: productBundle?.bundle?.type.manufacturer?.title ?? '',
        }}
        schemaOrg={{ enable: false }}
      />

      {productBundle?.bundle?.type.mainCategory && (
        <ProductHeader
          category={productBundle.bundle?.type.mainCategory as ProductType['mainCategory']}
          productTitle={generateProductTitle(productBundle.bundle.type, productBundle.bundle.properties)}
        />
      )}

      <DetailInformation productBundle={productBundle} />

      {/*
       * We will display the top-information component with the rental-station select when the user opens a product-page link with a rentalStation-id that does not exist at the moment ot a rental-station that has no available products of the product-type.
       * Reason:
       * Otherwise, we will show the error-page without an alternative rental-station. A redirect inside the getServerSideProps is not possible in Next.js when the user will navigation internal to that page.
      */}
      {0 < (productBundle?.bundle?.availableRentalStations.length ?? 0) && (
        <DisplayProductDetailLayout
          productBundle={productBundle}
          productPrice={productPrice}
          zones={props.content}
        />
      )}

      {false === hasAvailableProducts && (
        <NoProductsAvailableLayout categoryUriComponent={productBundle?.bundle?.type.mainCategory?.id ?? ''} />
      )}

      {props?.content && (
        <ContentContainerMax>
          <Zones
            zones={props.content.filter(zones => 'ComponentContentTextWithImage' !== zones.__typename && 'ComponentContentUspProduct' !== zones.__typename)}
          />
        </ContentContainerMax>
      )}
    </>
  );
};

export const getServerSideProps = async (
  props: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<IProductProps | IDigandoErrorProps>> => {
  // startProfiler('product').catch((e) => console.error('Profiler error', e));

  const nonWorkingDays = generateWeekends();

  const { locale, query, res } = props;
  const cookies = getCookies({ req: props.req, res });
  const filterValues = getDefaultFilterValues(nonWorkingDays, null, query?.from as string ?? null, query?.to as string ?? null);
  const rentalStationIdentifier = query?.rentalStationId as string || null;
  const { tenantKey: queryTenantKey = null } = props.query;

  let variants: IPropertyFilter[] = [];

  try {
    const variantsJSON: string = (query?.variants as string) ?? JSON.stringify([]);

    variants = JSON.parse(variantsJSON) as IPropertyFilter[];
  } catch (error: unknown) {
    // Ignore wrong variants
  }
  try {
    const variables = generateProductBundleVariables(
      {
        gte: filterValues.dateRange.gte,
        lte: filterValues.dateRange.lte,
      },
      filterValues.geoPoint,
      query.product as string,
      variants,
      variants,
      rentalStationIdentifier,
      queryTenantKey as string | null,
      null,
      {
        rentalStationIdentifier
      },
      {
        rentalStationIdentifier
      }
    );

    const session: SessionType = await getServerSession(props.req, props.res, authOptions());

    const productPageReq = ssrProduct.getServerPage({
      variables: variables,
      context: { accessToken: session?.accessToken, cookies },
    });

    const [productPageRes, ssrNavigation] = await Promise.all([
      productPageReq,
      serverSideNavigation(),
    ]);

    const productPage = productPageRes.props.data.productPage?.data?.attributes;

    if (!productPage) {
      console.error('No product page found');

      return {
        notFound: true,
      };
    }

    // Handle Redirect when the selected product is not available at the selected rental-station
    const availableRentalStationIds = productPageRes.props.data?.productBundle?.bundle?.availableRentalStations?.map(station => station.id) ?? [];
    if (productPageRes.props.data?.productBundle?.bundle?.type?.productPageUrl && rentalStationIdentifier && false === availableRentalStationIds.includes(rentalStationIdentifier)) {
      // redirect to the first available rental-station
      const url = new URL(productPageRes.props.data.productBundle.bundle.type.productPageUrl, 'https://www.digando.com');

      if (0 < availableRentalStationIds.length) {
        url.searchParams.set('rentalStationId', availableRentalStationIds[0]);
      }

      if (query?.from) {
        url.searchParams.set('from', query.from as string);
      }

      if (query?.to) {
        url.searchParams.set('to', query.to as string);
      }

      if (query?.shippingOption) {
        url.searchParams.set('shippingOption', query.shippingOption as string);
      }

      return {
        redirect: {
          permanent: false,
          destination: `${url.pathname}${url.search}`
        },
      };
    }

    const hasAvailableProducts = 0 < (productPageRes.props.data.productBundle?.bundle?.availableProducts ?? 0);

    if (null === productPageRes.props.data.productBundle) {
      console.error(`No product found for ${query.product}`);

      return {
        notFound: true,
      };
    }

    const productBundle = productPageRes.props.data.productBundle?.bundle;

    // if importAsUriComponent exists, redirect to the that url
    const importAsUriComponent = productBundle?.type.importAsUriComponent;
    if (importAsUriComponent) {
      return {
        redirect: {
          permanent: true,
          destination: importAsUriComponent,
        },
      };
    }

    const queryCanonicalLink = getCanonicalQueryParams({});
    const canonicals: ICanonical[] = [];

    canonicals.push({
      url: productPageRes?.props?.data?.productBundle?.bundle?.type.productPageUrl + queryCanonicalLink,
      lang: props.locale as Languages,
      alternate: false,
    });

    const seoImage = productPageRes?.props?.data?.productBundle?.bundle?.type?.images?.[0];
    let relatedProducts: ProductSearchItemFragment[] = [];

    if (false === hasAvailableProducts) {
      const resRelatedProducts = await ssrRelatedProducts.getServerPage({
        variables: {
          categoryUriComponent: productBundle?.type?.mainCategory?.uriComponent ?? '',
          geoPoint: filterValues.geoPoint,
          dateRange: {
            gte: filterValues.dateRange.gte.format('YYYY-MM-DD'),
            lte: filterValues.dateRange.lte.format('YYYY-MM-DD'),
          },
        },
        context: { accessToken: session?.accessToken },
      });

      relatedProducts = resRelatedProducts.props.data?.ProductSearch_relatedProducts?.products ?? [];
    }

    const cache = await getZoneApiCache(productPage?.content as AllZonesFragment[] ?? []);

    /** Return all SSR data */
    return {
      props: {
        apolloState: {
          ...cache.apolloState,
          ...ssrNavigation.apolloState,
          ROOT_QUERY: {
            ...cache?.apolloState?.ROOT_QUERY ?? {},
            ...ssrNavigation.apolloState?.ROOT_QUERY ?? {},
          },
        },
        data: {
          hasAvailableProducts,
          productBundle: productPageRes.props.data.productBundle,
          productPrice: productPageRes.props.data.productPrice,
          relatedProducts,
        },
        ...(await serverSideTranslations(locale ?? 'de', [...defaultTranslationNamespaces, 'product-page', 'checkout-page'], i18nextConfig)),
        pageTitle: productPage.seo?.title ?? productBundle?.type.title ?? '',
        seo: {
          title: productPage.seo?.title ?? productBundle?.type.title ?? '',
          description: productPage.seo?.description ?? '',
          url: productPageRes.props.data.productBundle?.bundle?.type.productPageUrl ?? '/',
          image: {
            url: seoImage?.url ?? '',
            copyright: 'Digando GmbH',
            alternativeText: productBundle?.type.title ?? '',
            width: seoImage?.metadata.width ?? 0,
            height: seoImage?.metadata.height ?? 0,
          },
          canonicals,
          dateModified: dayjs().toISOString(),
          datePublished: productPageRes.props.data.productBundle?.bundle?.type.firstPublicationDate ?? dayjs().toISOString(),
        },
        content: productPage?.content as AllZonesFragment[] ?? [],
        session: JSON.parse(JSON.stringify(session))
      },
    };
  } catch (error: unknown) {
    console.error('Error in getServerSideProps', error);
    res.statusCode = getStatusCode(error as IError);

    if (404 === res.statusCode) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default Product;
